import { supabase } from './supabase';
import { apiKeyService } from './apiKeyService';
import { SecurityService } from './securityService';
import { requestJson } from './apiClient';
import { 
  DataForSEOSettings, 
  RankedKeywordData, 
  KeywordData,
  validators 
} from './types';
import { encodeBase64 } from './base64Utils';

export class DataForSEOService {
  // Logging controls for readability
  private static readonly LOG_PREFIX = '[Keywords]';
  private static readonly DEBUG_VERBOSE = false; // set true to see full item payloads
  /**
   * Analyse un domaine pour récupérer ses mots-clés
   * Si aucune recherche n'a été effectuée, génère des mots-clés fictifs basés sur le nom de la société
   */
  async analyzeDomain(domain: string, prospectId?: string, forceRefresh: boolean = false): Promise<KeywordData[]> {
    try {
      
      // Si un ID prospect est fourni, vérifier si des données existent déjà
      if (prospectId && !forceRefresh) {
        const { data: storedData, error } = await supabase
          .from('keyword_analysis_results')
          .select('*')
          .eq('prospect_id', prospectId)
          .maybeSingle();
          
        if (!error && storedData && storedData.ranked_keywords && storedData.ranked_keywords.length > 0) {
          
          // Convertir les données au format KeywordData
          const formattedKeywords: KeywordData[] = storedData.ranked_keywords.map((kw: RankedKeywordData) => ({
            keyword: kw.keyword,
            position: kw.rank_group,
            search_volume: kw.search_volume,
            url: kw.url
          }));
          
          return formattedKeywords;
        }
      }
      
      // Récupérer les mots-clés depuis DataForSEO
      const rankedKeywords = await this.fetchRankedKeywords(domain, prospectId);
      
      if (rankedKeywords && rankedKeywords.length > 0) {
        
        // Convertir les données au format KeywordData
        const formattedKeywords: KeywordData[] = rankedKeywords.map(kw => ({
          keyword: kw.keyword,
          position: kw.rank_group,
          search_volume: kw.search_volume,
          url: kw.url
        }));
        
        return formattedKeywords;
      }
      
      // Si aucun mot-clé n'est trouvé, générer des mots-clés fictifs
      return this.generateMockKeywords(domain);
    } catch (error) {
      console.error('Erreur lors de l\'analyse du domaine:', error);
      return this.generateMockKeywords(domain);
    }
  }
  
  /**
   * Génère des mots-clés fictifs basés sur le nom de la société et le domaine
   */
  generateMockKeywords(domain: string, companyName?: string): KeywordData[] {
    const domainWithoutExtension = domain.replace(/\.(com|ch|fr|net|org|io|co).*$/, '');
    const name = companyName || domainWithoutExtension;
    const industry = 'services';
    
    // Générer des mots-clés basés sur le nom et l'industrie
    const mockKeywords: KeywordData[] = [
      { keyword: name, position: Math.floor(Math.random() * 5) + 1, search_volume: Math.floor(Math.random() * 500) + 50 },
      { keyword: `${name} ${industry}`, position: Math.floor(Math.random() * 10) + 5, search_volume: Math.floor(Math.random() * 300) + 20 },
      { keyword: `${name} avis`, position: Math.floor(Math.random() * 15) + 10, search_volume: Math.floor(Math.random() * 200) + 10 },
      { keyword: `${name} contact`, position: Math.floor(Math.random() * 20) + 15, search_volume: Math.floor(Math.random() * 100) + 5 },
      { keyword: `${industry} suisse`, position: Math.floor(Math.random() * 50) + 20, search_volume: Math.floor(Math.random() * 1000) + 100 }
    ];
    
    return mockKeywords;
  }
  
  /**
   * Génère des mots-clés classés fictifs pour le format RankedKeywordData
   */
  generateMockRankedKeywords(domain: string, companyName?: string): RankedKeywordData[] {
    const domainWithoutExtension = domain.replace(/\.(com|ch|fr|net|org|io|co).*$/, '');
    const name = companyName || domainWithoutExtension;
    const industry = 'services';
    
    // Générer des mots-clés classés fictifs
    const mockRankedKeywords: RankedKeywordData[] = [
      { 
        keyword: name, 
        rank_group: Math.floor(Math.random() * 5) + 1,
        rank_absolute: Math.floor(Math.random() * 5) + 1,
        url: `https://${domain}`,
        search_volume: Math.floor(Math.random() * 500) + 50,
        cpc: Math.random() * 2,
        competition: Math.random(),
        etv: Math.floor(Math.random() * 100),
        estimated_paid_traffic_cost: Math.floor(Math.random() * 200),
        title: `${name} - Accueil`,
        description: `Découvrez ${name}, expert en ${industry}.`
      },
      { 
        keyword: `${name} ${industry}`, 
        rank_group: Math.floor(Math.random() * 10) + 5,
        rank_absolute: Math.floor(Math.random() * 10) + 5,
        url: `https://${domain}/services`,
        search_volume: Math.floor(Math.random() * 300) + 20,
        cpc: Math.random() * 1.5,
        competition: Math.random(),
        etv: Math.floor(Math.random() * 80),
        estimated_paid_traffic_cost: Math.floor(Math.random() * 150),
        title: `${name} - Nos services`,
        description: `Services professionnels de ${name} dans le domaine ${industry}.`
      },
      { 
        keyword: `${name} avis`, 
        rank_group: Math.floor(Math.random() * 15) + 10,
        rank_absolute: Math.floor(Math.random() * 15) + 10,
        url: `https://${domain}/temoignages`,
        search_volume: Math.floor(Math.random() * 200) + 10,
        cpc: Math.random() * 1,
        competition: Math.random(),
        etv: Math.floor(Math.random() * 50),
        estimated_paid_traffic_cost: Math.floor(Math.random() * 100),
        title: `${name} - Témoignages clients`,
        description: `Découvrez les avis de nos clients sur ${name}.`
      },
      { 
        keyword: `${name} contact`, 
        rank_group: Math.floor(Math.random() * 20) + 15,
        rank_absolute: Math.floor(Math.random() * 20) + 15,
        url: `https://${domain}/contact`,
        search_volume: Math.floor(Math.random() * 100) + 5,
        cpc: Math.random() * 0.8,
        competition: Math.random(),
        etv: Math.floor(Math.random() * 30),
        estimated_paid_traffic_cost: Math.floor(Math.random() * 80),
        title: `${name} - Contact`,
        description: `Contactez ${name} pour vos besoins en ${industry}.`
      },
      { 
        keyword: `${industry} suisse`, 
        rank_group: Math.floor(Math.random() * 50) + 20,
        rank_absolute: Math.floor(Math.random() * 50) + 20,
        url: `https://${domain}`,
        search_volume: Math.floor(Math.random() * 1000) + 100,
        cpc: Math.random() * 3,
        competition: Math.random(),
        etv: Math.floor(Math.random() * 200),
        estimated_paid_traffic_cost: Math.floor(Math.random() * 300),
        title: `${name} - ${industry} en Suisse`,
        description: `${name} offre des services de ${industry} de qualité en Suisse.`
      }
    ];
    
    return mockRankedKeywords;
  }
  
  /**
   * Récupère les identifiants DataForSEO depuis la table api_keys
   */
  async getSettings(): Promise<DataForSEOSettings | null> {
    try {
      // Utiliser getApiConfig au lieu de getApiKey pour obtenir l'objet complet
      const config = await apiKeyService.getApiConfig('dataforseo');
      
      if (config) {
        // Déchiffrer la clé API si elle existe
        const decryptedKey = config.api_key ? await SecurityService.decryptApiKey(config.api_key) : '';
        
        return {
          // Récupérer le login depuis metadata.login
          login: config.metadata?.login || '',
          // Utiliser la clé déchiffrée
          api_key: decryptedKey
        };
      }
      
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération des identifiants DataForSEO:', error);
      return null;
    }
  }
  
  /**
   * Enregistre les identifiants DataForSEO dans la table unifiée api_keys
   */
  async saveSettings(settings: DataForSEOSettings): Promise<boolean> {
    try {
      // Valider les données avant sauvegarde
      const validation = validators.dataForSEOSettings.validate(settings);
      if (!validation.success) {
        console.error('Données DataForSEO invalides:', validation.error);
        return false;
      }
      
      // Nettoyer la clé API (supprimer les espaces)
      const cleanApiKey = validation.data.api_key.trim();
      
      // Vérifier le format de la clé API (uniquement des caractères alphanumériques)
      if (!/^[a-zA-Z0-9]+$/.test(cleanApiKey)) {
        console.error('Format de clé API invalide');
        return false;
      }
      
      // Sauvegarder la clé avec le login comme métadonnée
      const result = await apiKeyService.saveApiKey(
        'dataforseo', 
        cleanApiKey, 
        { login: validation.data.login }
      );
      
      return result;
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement des identifiants DataForSEO:', error);
      return false;
    }
  }
  
  /**
   * Valide les identifiants DataForSEO en effectuant un appel de test à l'API
   * Utilise spécifiquement l'endpoint des mots-clés classés pour garantir que les identifiants fonctionnent pour cet endpoint
   */
  async validateCredentials(settings: DataForSEOSettings): Promise<boolean> {
    try {
      if (!settings.login || !settings.api_key) {
        console.error('Identifiants DataForSEO incomplets');
        return false;
      }

      // Nettoyer et préparer les identifiants
      const cleanLogin = settings.login.trim();
      const cleanApiKey = settings.api_key.trim();
      const rawAuthString = `${cleanLogin}:${cleanApiKey}`;

      // Encoder en base64 de manière robuste
      const authString = encodeBase64(rawAuthString);
      
      // Utiliser le même endpoint que pour la récupération des mots-clés classés
      const url = "https://api.dataforseo.com/v3/dataforseo_labs/google/ranked_keywords/live";
      const headers = {
        "Authorization": `Basic ${authString}`,
        "Content-Type": "application/json"
      };

      const response = await requestJson<any>(url, {
        method: "POST",
        headers: {
          "Authorization": `Basic ${authString}`,
        },
        body: [{
          "target": "smart-impact.ch",
          "location_name": "Switzerland",
          "language_name": "French",
          "limit": 1
        }]
      });
      
      
      if (response.ok) {
        const data = response.data;
        const isValid = data.status_code === 20000;
        return isValid;
      }
      
      // En cas d'échec, essayer de lire le corps de la réponse pour plus de détails
      console.error('Détails de l\'erreur de validation:', response.errorText || '');
      
      return false;
    } catch (error) {
      console.error('Erreur lors de la validation des identifiants DataForSEO:', error);
      return false;
    }
  }
  
  /**
   * Fonction de test pour vérifier si l'API DataForSEO fonctionne correctement
   * Utile pour déboguer les problèmes d'API
   */
  async testRankedKeywordsApi(domain: string = 'smart-impact.ch'): Promise<any> {
    try {
      // Récupérer les identifiants DataForSEO
      const settings = await this.getSettings();
      if (!settings || !settings.login || !settings.api_key) {
        return { success: false, error: 'Identifiants DataForSEO non configurés ou incomplets' };
      }
      
      // Nettoyer le domaine et préparer la requête
      const cleanDomain = domain.trim().toLowerCase();
      const authString = btoa(`${settings.login}:${settings.api_key}`);
      
      // Préparer les données pour la requête
      const data = JSON.stringify([{
        "target": cleanDomain,
        "location_name": "Switzerland",
        "language_name": "French",
        "limit": 5
      }]);

      // Envoyer la requête à l'API DataForSEO Labs
      const response = await requestJson<any>("https://api.dataforseo.com/v3/dataforseo_labs/google/ranked_keywords/live", {
        method: "POST",
        headers: {
          "Authorization": `Basic ${authString}`
        },
        body: JSON.parse(data)
      });
      
      const responseInfo = {
        status: response.status,
        statusText: response.ok ? 'OK' : 'ERROR'
      } as any;
      
      
      if (!response.ok) {
        return { 
          success: false, 
          error: 'Erreur HTTP', 
          response: responseInfo,
          details: response.errorText || ''
        };
      }
      
      const jsonResponse = response.data;
      
      return {
        success: true,
        response: responseInfo,
        data: jsonResponse
      };
    } catch (error) {
      console.error('Exception lors du test de l\'API:', error);
      return { 
        success: false, 
        error: 'Exception', 
        details: error instanceof Error ? error.message : String(error)
      };
    }
  }
  
  /**
   * Récupère les mots-clés classés (ranked keywords) pour un domaine donné depuis DataForSEO Labs
   * @param domain Le domaine à analyser (ex: example.com)
   * @param prospectId ID du prospect (optionnel)
   * @returns Un tableau de mots-clés classés ou null en cas d'erreur
   */
  async fetchRankedKeywords(domain: string, prospectId?: string): Promise<RankedKeywordData[] | null> {
    try {
      
      // Récupérer les identifiants DataForSEO
      const settings = await this.getSettings();
      if (!settings || !settings.login || !settings.api_key) {
        console.error('Identifiants DataForSEO non configurés ou incomplets');
        // Utiliser des données simulées si les identifiants ne sont pas disponibles
        const mockKeywords = this.generateMockRankedKeywords(domain);
        if (prospectId && mockKeywords.length > 0) {
          await this.saveRankedKeywords(domain, prospectId, mockKeywords);
        }
        return mockKeywords;
      }
      
      // Nettoyer le domaine et préparer la requête
      const cleanDomain = domain.trim().toLowerCase();
      const authString = btoa(`${settings.login}:${settings.api_key}`);
      
      // Préparer les données pour la requête
      const data = JSON.stringify([{
        "target": cleanDomain,
        "location_name": "Switzerland",
        "language_name": "French",
        "limit": 20
      }]);
      
      // Envoyer la requête à l'API DataForSEO Labs
      const response = await requestJson<any>("https://api.dataforseo.com/v3/dataforseo_labs/google/ranked_keywords/live", {
        method: "POST",
        headers: {
          "Authorization": `Basic ${authString}`
        },
        body: JSON.parse(data)
      });
      
      if (!response.ok) {
        console.error('Erreur lors de la récupération des mots-clés classés:', response.status);
        console.error('Détails de l\'erreur:', response.errorText || '');
        // Utiliser des données simulées en cas d'erreur
        const mockKeywords = this.generateMockRankedKeywords(domain);
        if (prospectId && mockKeywords.length > 0) {
          await this.saveRankedKeywords(domain, prospectId, mockKeywords);
        }
        return mockKeywords;
      }
      
      const jsonResponse = response.data;
      
      if (jsonResponse.status_code !== 20000) {
        console.error('Erreur API DataForSEO:', jsonResponse.status_message);
        // Utiliser des données simulées en cas d'erreur
        const mockKeywords = this.generateMockRankedKeywords(domain);
        if (prospectId && mockKeywords.length > 0) {
          await this.saveRankedKeywords(domain, prospectId, mockKeywords);
        }
        return mockKeywords;
      }
      
      // Extraire et transformer les mots-clés classés
      const rankedKeywords: RankedKeywordData[] = [];
      
      if (jsonResponse.tasks && jsonResponse.tasks.length > 0 && 
          jsonResponse.tasks[0].result && jsonResponse.tasks[0].result.length > 0) {
        const result = jsonResponse.tasks[0].result[0];
        
        if (result.items && result.items.length > 0) {
          
          // Transformer les données depuis la structure DataForSEO
          const sampleSummaries: string[] = [];
          result.items.forEach((item: any, index: number) => {
            try {
              if (DataForSEOService.DEBUG_VERBOSE) {
              }
              
              const keywordData = item.keyword_data;
              const keywordInfo = keywordData?.keyword_info || {};
              
              if (keywordData && keywordData.keyword) {
                // Calculer le rang basé sur les métriques organiques
                let calculatedRank = 95; // Valeur par défaut
                
                // Chercher dans les métriques organiques pour déterminer la position
                if (result.metrics && result.metrics.organic) {
                  const organic = result.metrics.organic;
                  if (organic.pos_1 > 0) calculatedRank = 1;
                  else if (organic.pos_2_3 > 0) calculatedRank = 2;
                  else if (organic.pos_4_10 > 0) calculatedRank = 7;
                  else if (organic.pos_11_20 > 0) calculatedRank = 15;
                  else if (organic.pos_21_30 > 0) calculatedRank = 25;
                  else if (organic.pos_31_40 > 0) calculatedRank = 35;
                  else if (organic.pos_41_50 > 0) calculatedRank = 45;
                  else if (organic.pos_51_60 > 0) calculatedRank = 55;
                  else if (organic.pos_61_70 > 0) calculatedRank = 65;
                  else if (organic.pos_71_80 > 0) calculatedRank = 75;
                  else if (organic.pos_81_90 > 0) calculatedRank = 85;
                  else if (organic.pos_91_100 > 0) calculatedRank = 95;
                }
                
                const keywordEntry = {
                  keyword: keywordData.keyword,
                  rank_group: calculatedRank,
                  rank_absolute: calculatedRank,
                  url: item.url || `https://${domain}`,
                  search_volume: keywordInfo.search_volume || 0,
                  cpc: keywordInfo.cpc || 0,
                  competition: keywordInfo.competition || 0,
                  etv: item.etv || 0,
                  estimated_paid_traffic_cost: item.estimated_paid_traffic_cost || 0,
                  title: item.title || '',
                  description: item.description || ''
                };
                
                if (sampleSummaries.length < 3) {
                  sampleSummaries.push(`"${keywordEntry.keyword}" rank=${keywordEntry.rank_group} url=${keywordEntry.url}`);
                }
                rankedKeywords.push(keywordEntry);
              }
            } catch (err) {
              console.error('Erreur lors du traitement d\'un mot-clé:', err, item);
            }
          });
          // Log a concise summary instead of every item
          if (sampleSummaries.length > 0) {
          } else {
          }
        } else {
          console.warn('Pas de propriété items dans le résultat ou items vide!');
        }
      }
      
      // Si aucun mot-clé n'est trouvé, utiliser des données simulées
      if (rankedKeywords.length === 0) {
        const mockKeywords = this.generateMockRankedKeywords(domain);
        if (prospectId && mockKeywords.length > 0) {
          await this.saveRankedKeywords(domain, prospectId, mockKeywords);
        }
        return mockKeywords;
      }
      
      // Sauvegarder les résultats si un ID prospect est fourni
      if (prospectId && rankedKeywords.length > 0) {
        await this.saveRankedKeywords(domain, prospectId, rankedKeywords);
      }
      
      return rankedKeywords;
    } catch (error) {
      console.error('Exception lors de la récupération des mots-clés classés:', error);
      // En cas d'erreur, retourner des données simulées
      const mockKeywords = this.generateMockRankedKeywords(domain);
      if (prospectId && mockKeywords.length > 0) {
        await this.saveRankedKeywords(domain, prospectId, mockKeywords);
      }
      return mockKeywords;
    }
  }
  
  /**
   * Sauvegarde les mots-clés classés dans la table dédiée
   */
  async saveRankedKeywords(domain: string, prospectId: string, keywords: RankedKeywordData[]): Promise<boolean> {
    if (!prospectId) {
      console.error('Impossible de sauvegarder les mots-clés classés: ID prospect manquant');
      return false;
    }
    
    try {
      
      // Vérifier si les mots-clés semblent être des données réelles ou des mocks
      const seemsReal = !keywords.some(k => k.keyword.includes('keyword') && k.keyword.match(/\d+/));
      
      // D'abord, supprimer les enregistrements existants
      const { error: deleteError } = await supabase
        .from('keyword_analysis_results')
        .delete()
        .match({
          prospect_id: prospectId,
          domain: domain,
          analysis_type: 'ranked_keywords'
        });
      
      if (deleteError) {
        console.error('Erreur lors de la suppression des anciens mots-clés:', deleteError);
        return false;
      }
      
      // Ensuite, insérer le nouvel enregistrement
      const { error: insertError } = await supabase
        .from('keyword_analysis_results')
        .insert({
          prospect_id: prospectId,
          domain: domain,
          ranked_keywords: keywords,
          analysis_type: 'ranked_keywords',
          analyzed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      
      if (insertError) {
        console.error('Erreur lors de l\'insertion des mots-clés classés:', insertError);
        return false;
      }
      
      return true;
    } catch (err) {
      console.error('Exception lors de la sauvegarde des mots-clés classés:', err);
      return false;
    }
  }
}

export const dataforseoService = new DataForSEOService();
